import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkles, MessageCircle, Save, Trash2, Send, Calendar, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function OffersPanel() {
  const [form, setForm] = useState({ service: "Limpieza Facial", discount: "20%", valid_until: "", extra_notes: "" });
  const [gen, setGen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offers, setOffers] = useState([]);
  const [patients, setPatients] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [scheduleFor, setScheduleFor] = useState({});
  const [metaConfigured, setMetaConfigured] = useState(false);

  const load = async () => {
    try {
      const [o, p, m] = await Promise.all([api.get("/offers"), api.get("/patients"), api.get("/meta/status")]);
      setOffers(o.data); setPatients(p.data);
      setMetaConfigured(m.data.configured && m.data.token_check?.ok);
    } catch (_) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!form.service || !form.discount || !form.valid_until) { toast.error("Completa los campos"); return; }
    setLoading(true); setGen(null);
    try {
      const r = await api.post("/ai/offer", form);
      setGen(r.data); toast.success("Oferta generada");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setLoading(false); }
  };

  const save = async () => {
    if (!gen) return;
    try {
      const r = await api.post("/offers", { title: gen.title, message: gen.message, discount: form.discount, valid_until: form.valid_until, service: form.service });
      toast.success("Oferta guardada");
      setOffers([r.data, ...offers]);
      setSelectedOffer(r.data);
    } catch { toast.error("Error al guardar"); }
  };

  const remove = async (id) => {
    await api.delete(`/offers/${id}`);
    setOffers(offers.filter((o) => o.id !== id));
    if (selectedOffer?.id === id) setSelectedOffer(null);
  };

  const schedule = async (id) => {
    const dt = scheduleFor[id];
    if (!dt) { toast.error("Elige fecha y hora"); return; }
    try {
      await api.patch(`/offers/${id}/schedule`, { scheduled_for: new Date(dt).toISOString() });
      toast.success("Programada. Se publicará automáticamente en Facebook.");
      load();
    } catch { toast.error("Error al programar"); }
  };

  const publishNow = async (id) => {
    if (!metaConfigured) { toast.error("Meta no está configurado. Configúralo en Reels."); return; }
    if (!confirm("¿Publicar esta oferta en Facebook ahora?")) return;
    try {
      toast.info("Publicando…");
      await api.post(`/meta/publish-now/offer/${id}`);
      toast.success("Publicado en Facebook");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al publicar"); }
  };

  const buildWa = (phone, name, message) => {
    const text = message.replace(/{nombre}/gi, name);
    const clean = String(phone).replace(/\D/g, "");
    const prefixed = clean.startsWith("52") ? clean : `52${clean}`;
    return `https://wa.me/${prefixed}?text=${encodeURIComponent(text)}`;
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="offers-panel">
      <div className="lg:col-span-5 space-y-6">
        <div className="bg-white rounded-2xl border border-[#E6E2DC] p-7 space-y-5">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">IA Generativa</div>
            <h3 className="font-display text-2xl text-[#1A1C1A]">Crear oferta WhatsApp</h3>
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Servicio</Label>
            <Input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid="offer-service" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Descuento</Label>
              <Input value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} placeholder="20% o $500" data-testid="offer-discount" />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Vigencia</Label>
              <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="offer-valid" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Notas</Label>
            <Textarea rows={2} value={form.extra_notes} onChange={(e) => setForm({ ...form, extra_notes: e.target.value })} placeholder="Ej. Incluye limpieza dental + valoración" data-testid="offer-notes" />
          </div>
          <Button onClick={generate} disabled={loading} className="w-full rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white h-11 text-xs uppercase tracking-[0.2em]" data-testid="offer-generate">
            <Sparkles className="w-4 h-4 mr-2" /> {loading ? "Generando..." : "Generar oferta"}
          </Button>
        </div>

        {gen && (
          <div className="bg-white rounded-2xl border border-[#E6E2DC] p-7" data-testid="offer-result">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-2">Vista previa</div>
            <h4 className="font-display text-xl text-[#1A1C1A] mb-3">{gen.title}</h4>
            <div className="bg-[#F4F0EB] rounded-xl p-5 text-sm whitespace-pre-line text-[#1A1C1A]">{gen.message}</div>
            <Button onClick={save} className="mt-4 bg-[#A69076] hover:bg-[#1A1C1A] text-white rounded-full text-xs uppercase tracking-[0.2em]" data-testid="offer-save">
              <Save className="w-3.5 h-3.5 mr-1" /> Guardar oferta
            </Button>
          </div>
        )}
      </div>

      <div className="lg:col-span-7 space-y-6">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-3">Ofertas guardadas ({offers.length})</div>
          {offers.length === 0 && <div className="bg-white rounded-2xl border border-[#E6E2DC] p-8 text-center text-sm text-[#5C5E5C]" data-testid="offers-empty">Aún no hay ofertas. Crea la primera.</div>}
          <div className="space-y-3">
            {offers.map((o) => (
              <div key={o.id} className={`bg-white rounded-2xl border p-5 transition-colors ${selectedOffer?.id === o.id ? "border-[#A69076]" : "border-[#E6E2DC]"}`} data-testid={`offer-${o.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076]">{o.service} · {o.discount} · hasta {o.valid_until}</div>
                      {o.status === "publicada" && <span className="text-[9px] tracking-widest px-1.5 py-0.5 rounded-full bg-[#1A1C1A] text-white">PUBLICADA</span>}
                      {o.scheduled_for && o.status !== "publicada" && <span className="text-[9px] tracking-widest px-1.5 py-0.5 rounded-full bg-[#D9CDBC] text-[#1A1C1A]">PROGRAMADA</span>}
                      {o.status === "publish_failed" && <span className="text-[9px] tracking-widest px-1.5 py-0.5 rounded-full bg-[#F5E0E0] text-[#965D5D]">ERROR</span>}
                    </div>
                    <div className="font-display text-lg text-[#1A1C1A] mt-1">{o.title}</div>
                    <p className="text-xs text-[#5C5E5C] mt-2 line-clamp-3 whitespace-pre-line">{o.message}</p>
                    {o.scheduled_for && (
                      <div className="text-[10px] text-[#5C5E5C] mt-2 flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(o.scheduled_for).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}</div>
                    )}
                    {o.last_error && <p className="text-[10px] text-[#965D5D] mt-1">⚠ {o.last_error}</p>}
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <Button size="sm" onClick={() => setSelectedOffer(o)} variant={selectedOffer?.id === o.id ? "default" : "outline"} className="text-xs" data-testid={`offer-select-${o.id}`}>
                      <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
                    </Button>
                    {o.status !== "publicada" && (
                      <Button size="sm" className="bg-[#A69076] hover:bg-[#1A1C1A] text-white text-xs" onClick={() => publishNow(o.id)} data-testid={`offer-publish-${o.id}`}>
                        <Send className="w-3 h-3 mr-1" /> Publicar FB
                      </Button>
                    )}
                    <button onClick={() => remove(o.id)} className="p-1.5 text-[#965D5D] hover:bg-[#F5E0E0] rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                {o.status !== "publicada" && (
                  <div className="flex gap-1.5 mt-3 pt-3 border-t border-[#E6E2DC]">
                    <Input type="datetime-local" min={new Date().toISOString().slice(0,16)} className="h-8 text-xs flex-1" value={scheduleFor[o.id] || ""} onChange={(e) => setScheduleFor({ ...scheduleFor, [o.id]: e.target.value })} data-testid={`offer-schedule-input-${o.id}`} />
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => schedule(o.id)} data-testid={`offer-schedule-${o.id}`}>
                      <Calendar className="w-3 h-3 mr-1" /> Programar
                    </Button>
                  </div>
                )}
                {o.status === "publicada" && (
                  <div className="text-[10px] text-[#3a5a44] flex items-center gap-1 mt-2"><CheckCircle2 className="w-3 h-3" /> Publicado en Facebook</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {selectedOffer && (
          <div className="bg-white rounded-2xl border border-[#A69076] p-7" data-testid="offer-send-panel">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Enviar por WhatsApp</div>
            <h4 className="font-display text-2xl text-[#1A1C1A] mb-4">{selectedOffer.title}</h4>
            {patients.length === 0 ? (
              <div className="text-sm text-[#5C5E5C]">Agrega pacientes en la sección Pacientes para enviarles ofertas.</div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                {patients.map((p) => (
                  <div key={p.id} className="flex items-center justify-between bg-[#FAFAFA] rounded-lg px-4 py-3 border border-[#E6E2DC]">
                    <div>
                      <div className="text-sm font-medium">{p.name}</div>
                      <div className="text-xs text-[#5C5E5C]">{p.phone}</div>
                    </div>
                    <a href={buildWa(p.phone, p.name, selectedOffer.message)} target="_blank" rel="noreferrer" data-testid={`send-offer-${p.id}`}>
                      <Button size="sm" className="bg-[#7A9180] hover:bg-[#1A1C1A] text-white text-xs rounded-full">
                        <Send className="w-3 h-3 mr-1" /> Enviar
                      </Button>
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
